import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  SparklesIcon, 
  ArrowRightCircleIcon 
} from "@heroicons/react/24/solid";
import { renderStars } from "../utils/renderStars";
import { motion } from "framer-motion";
import config from '../utils/config';

const ToolCard = ({ tool }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const openToolDetails = () => {
    try {
      navigate("/tool", { state: { toolcard: tool } });
    } catch (error) {
      console.error('Error navigating to tool details:', error);
    }
  };

  // Animation variants
  const imageVariants = {
    hover: { 
      scale: 1.1,
      transition: { 
        duration: 0.4, 
        ease: "easeOut" 
      }
    },
    initial: { 
      scale: 1,
      transition: { 
        duration: 0.4, 
        ease: "easeOut" 
      }
    }
  };

  const overlayVariants = {
    hover: { 
      opacity: 0.2,
      transition: { duration: 0.3 }
    },
    initial: { 
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };

  const borderVariants = {
    hover: { 
      borderColor: "rgba(147, 197, 253, 0.8)",
      borderWidth: "2px",
      transition: { duration: 0.3 }
    },
    initial: { 
      borderColor: "rgba(0, 0, 0, 0)",
      borderWidth: "2px",
      transition: { duration: 0.3 }
    }
  };

  const gradientVariants = {
    hover: { 
      opacity: 0.5,
      transition: { duration: 0.3 }
    },
    initial: { 
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };

  const arrowVariants = {
    hover: { 
      x: 5,
      transition: { 
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    },
    initial: { 
      x: 0,
      transition: { 
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    }
  };

  return (
    <motion.div 
      className="group relative bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-lg"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ 
        y: -8,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
      }}
      transition={{ 
        type: "spring",
        stiffness: 300,
        damping: 20
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background Gradient Layer */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 z-0"
        variants={gradientVariants}
        animate={isHovered ? "hover" : "initial"}
      ></motion.div>

      {/* Tool Image Container */}
      <div className="relative h-48 overflow-hidden">
        <motion.img 
          src={`${config.IMG_URL}/images/tools/${tool.logo}` || 'https://img.freepik.com/premium-vector/robotic-logo-template-design-illustration-abstract-robot-web-icons-logo-robot-logo-that-lacks-energy_72830-10.jpg'} 
          alt={tool.name} 
          className="w-full h-full object-cover"
          variants={imageVariants}
          animate={isHovered ? "hover" : "initial"}
        />
        {/* Overlay */}
        <motion.div 
          className="absolute inset-0 bg-black"
          variants={overlayVariants}
          animate={isHovered ? "hover" : "initial"}
        ></motion.div>
      </div>

      {/* Tool Content */}
      <div className="relative z-10 p-5">
        <div className="flex justify-between items-center mb-2">
          <motion.h3 
            className="text-xl font-bold text-gray-800"
            animate={{ 
              color: isHovered ? "#1e40af" : "#1f2937",
              transition: { duration: 0.3 }
            }}
          >
            {tool.name}
          </motion.h3>
          {tool.isPremium && (
            <motion.div
              initial={{ scale: 1 }}
              animate={{ 
                scale: [1, 1.15, 1],
                transition: { 
                  repeat: Infinity,
                  duration: 2
                }
              }}
            >
              <SparklesIcon 
                className="w-6 h-6 text-yellow-500" 
                title="Premium Tool"
              />
            </motion.div>
          )}
        </div>

        {/* Rating Section */}
        <div className="mb-2">
          {tool.averageRating ? (
            <div className="flex items-center space-x-2">
              {renderStars(tool.averageRating)}
              <span className="text-sm text-gray-600">
                
              </span>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No ratings yet</p>
          )}
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {tool.description}
        </p>

        {/* Read More Button */}
        <motion.button 
          onClick={openToolDetails}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 
                     bg-blue-500 text-white rounded-lg 
                     hover:bg-blue-600 transition-colors duration-300 group/button"
          whileHover={{ 
            backgroundColor: "#2563eb",
            scale: 1.03
          }}
          whileTap={{ scale: 0.97 }}
        >
          <span>Explore Tool</span>
          <motion.div
            variants={arrowVariants}
            animate={isHovered ? "hover" : "initial"}
          >
            <ArrowRightCircleIcon className="w-5 h-5" />
          </motion.div>
        </motion.button>
      </div>

      {/* Hover Effect Border */}
      <motion.div 
        className="absolute inset-0 border-2 rounded-2xl pointer-events-none"
        variants={borderVariants}
        animate={isHovered ? "hover" : "initial"}
      ></motion.div>
    </motion.div>
  );
};

export default ToolCard;